import React, { createContext, useContext, useMemo, useCallback } from 'react'

import {
  useBatchMeetingStatuses,
  useBatchMeetingWarnings,
  type UseMeetingStatusReturn,
} from 'components/meetings/hooks'
import type { WarningKey } from 'components/meetings/utils'

import { useMeetingsRealtimeFeaturesEnabled } from '../hooks'

interface MeetingsContextValue {
  hasRealtimeFeatures: boolean
  getStatus: (uuid: string) => UseMeetingStatusReturn | undefined
  getWarning: (uuid: string) => WarningKey | null | undefined
}

interface MeetingsProviderProps {
  meetings?: any[]
  children: React.ReactNode
}

export const MeetingsContext = createContext<MeetingsContextValue | null>(null)

/**
 * Provides real-time meeting status and warning data to the meetings subtree.
 * Computes batch statuses and warnings for all visible meetings and exposes
 * lookups by UUID via getStatus and getWarning.
 */
export const MeetingsProvider: React.FC<MeetingsProviderProps> = ({
  meetings = [],
  children,
}) => {
  const hasRealtimeFeatures = useMeetingsRealtimeFeaturesEnabled()

  const flatMeetings = useMemo(
    () => (hasRealtimeFeatures
      ? meetings.flatMap((group: any) => (group.events || []).filter((e: any) => !e.placeholder))
      : []),
    [meetings, hasRealtimeFeatures]
  )

  const statusesMap = useBatchMeetingStatuses(flatMeetings)
  const warningsMap = useBatchMeetingWarnings(hasRealtimeFeatures ? meetings : [])

  const getStatus = useCallback(
    (uuid: string) => statusesMap.get(uuid),
    [statusesMap]
  )

  const getWarning = useCallback(
    (uuid: string) => warningsMap.get(uuid),
    [warningsMap]
  )

  const value = useMemo(
    () => ({ hasRealtimeFeatures, getStatus, getWarning }),
    [hasRealtimeFeatures, getStatus, getWarning]
  )

  return <MeetingsContext.Provider value={value}>{children}</MeetingsContext.Provider>
}

export const useMeetingsContext = () => useContext(MeetingsContext)
